/** Per-browser override for the default-on rounded-thumbnail renderer. */
export const PIXI_NO_STENCIL_MASK_STORAGE_KEY = 'martini_v3_no_stencil_mask'

export interface ThumbnailRendererOptions {
  /** Test/constructor override. Production callers omit this and use the sampled page flag. */
  useRoundedThumbnailMesh?: boolean
}

function parseBooleanFlag(value: string | null | undefined): boolean | undefined {
  if (value === undefined || value === null || value === '') return undefined
  const normalized = value.trim().toLowerCase()
  if (normalized === '1' || normalized === 'true' || normalized === 'on') return true
  if (normalized === '0' || normalized === 'false' || normalized === 'off') return false
  return undefined
}

/**
 * Resolve the flag with a QA override taking precedence over the build-time
 * environment default. Invalid values are ignored so a typo cannot silently
 * change renderers.
 *
 * DEFAULT ON since the 144-capture visual gate passed (72/72 renderer-parity;
 * see .context/visual-parity + docs/canvas-v3-performance-validation.md):
 * the rounded-mesh renderer batches thumbnails (draw calls 4,214 → 78 on the
 * 1,200-node board) and carries the world render group (GPU-side camera).
 * Kill switches: NEXT_PUBLIC_PIXI_NO_STENCIL_MASK=0 at build time, or
 * localStorage martini_v3_no_stencil_mask=0 per browser.
 */
export function resolvePixiNoStencilMaskFlag(
  envValue: string | null | undefined,
  storageValue: string | null | undefined,
): boolean {
  return parseBooleanFlag(storageValue) ?? parseBooleanFlag(envValue) ?? true
}

/** Read localStorage defensively; privacy modes can throw on property access. */
function readStorageOverride(): string | null | undefined {
  if (typeof window === 'undefined') return undefined
  try {
    return window.localStorage.getItem(PIXI_NO_STENCIL_MASK_STORAGE_KEY)
  } catch {
    return undefined
  }
}

/**
 * Sample once when this module is evaluated. Changing the QA override takes
 * effect on the next page load, never halfway through an existing scene.
 */
export const PIXI_NO_STENCIL_MASK = resolvePixiNoStencilMaskFlag(
  process.env.NEXT_PUBLIC_PIXI_NO_STENCIL_MASK,
  readStorageOverride(),
)
